import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';

import {
  Calculator,
  Copy,
  Check,
  RotateCcw,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  ArrowLeftRight,
  BookOpen,
  Binary,
  Atom,
  Ruler,
  Telescope,
  Microscope,
} from 'lucide-react';

import { scientificNotationFAQs } from '../data/mockData';

// Superscript conversion helper
const toSuperscript = (num) => {
  const superscripts = { '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '-': '⁻', '+': '⁺' };
  return String(num).split('').map(c => superscripts[c] || c).join('');
};

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
};

// Convert standard to scientific notation
const toScientificNotation = (num) => {
  if (num === 0) return { coefficient: 0, exponent: 0, formatted: '0 × 10⁰' };
  
  const sign = num < 0 ? -1 : 1;
  const absNum = Math.abs(num);
  const exponent = Math.floor(Math.log10(absNum));
  const coefficient = (absNum / Math.pow(10, exponent)) * sign;
  
  return {
    coefficient: parseFloat(coefficient.toPrecision(10)),
    exponent,
    formatted: `${coefficient.toPrecision(10).replace(/\.?0+$/, '')} × 10${toSuperscript(exponent)}`
  };
};

// Convert scientific notation to standard
const fromScientificNotation = (coefficient, exponent) => {
  return coefficient * Math.pow(10, exponent);
};

// Format large/small numbers
const formatStandardNumber = (num) => {
  if (!isFinite(num)) return 'Invalid';
  if (Math.abs(num) >= 1e15 || (Math.abs(num) < 1e-10 && num !== 0)) {
    return num.toExponential();
  }
  return num.toLocaleString('en-US', { maximumFractionDigits: 15 });
};

// Visual representation of decimal movement
const DecimalMoveVisual = ({ originalNumber, coefficient, exponent, mode }) => {
  const numStr = mode === 'toScientific' 
    ? originalNumber.toString() 
    : `${coefficient}`;
  
  const absExponent = Math.abs(exponent);
  const direction = exponent >= 0 ? 'left' : 'right';
  
  return (
    <div className="bg-muted/30 rounded-xl p-4 border">
      <div className="text-center mb-4">
        <h4 className="text-sm font-medium text-muted-foreground mb-2">
          {mode === 'toScientific' ? 'Converting to Scientific Notation' : 'Converting to Standard Form'}
        </h4>
        <div className="flex items-center justify-center gap-4">
          <div className="text-lg font-mono bg-background px-4 py-2 rounded-lg border">
            {mode === 'toScientific' ? formatStandardNumber(parseFloat(originalNumber)) : `${coefficient} × 10${toSuperscript(exponent)}`}
          </div>
          <ArrowLeftRight className="w-5 h-5 text-teal-500" />
          <div className="text-lg font-mono bg-teal-500/10 px-4 py-2 rounded-lg border border-teal-500/30 text-teal-600">
            {mode === 'toScientific' ? `${coefficient} × 10${toSuperscript(exponent)}` : formatStandardNumber(fromScientificNotation(coefficient, exponent))}
          </div>
        </div>
      </div>
      
      <div className="bg-background rounded-lg p-4">
        <p className="text-sm text-muted-foreground mb-3 text-center">
          Move the decimal point <span className="font-bold text-teal-500">{absExponent}</span> place{absExponent !== 1 ? 's' : ''} to the <span className="font-bold text-teal-500">{direction}</span>
        </p>
        
        <div className="flex items-center justify-center gap-1 font-mono text-2xl">
          {mode === 'toScientific' && parseFloat(originalNumber) !== 0 && (
            <div className="relative">
              <span className="text-foreground">{coefficient}</span>
              <div className="absolute -bottom-6 left-0 right-0 flex justify-center">
                <div className="flex items-center gap-1 text-xs text-teal-500">
                  {Array.from({ length: Math.min(absExponent, 5) }).map((_, i) => (
                    <span key={i}>→</span>
                  ))}
                  {absExponent > 5 && <span>({absExponent})</span>}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-8 grid grid-cols-3 gap-3 text-center text-sm">
          <div className="bg-muted/50 rounded-lg p-2">
            <div className="text-muted-foreground">Coefficient</div>
            <div className="font-mono font-bold text-teal-500">{coefficient}</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-2">
            <div className="text-muted-foreground">Base</div>
            <div className="font-mono font-bold">10</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-2">
            <div className="text-muted-foreground">Exponent</div>
            <div className="font-mono font-bold text-teal-500">{exponent}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Scale comparison visual
const ScaleComparison = ({ exponent }) => {
  const scales = [
    { exp: 24, name: 'Yotta', example: 'Observable universe mass', icon: Telescope },
    { exp: 21, name: 'Zetta', example: 'Stars in universe', icon: Telescope },
    { exp: 15, name: 'Peta', example: 'Distance to nearest star (km)', icon: Telescope },
    { exp: 12, name: 'Tera', example: 'US national debt ($)', icon: Binary },
    { exp: 9, name: 'Giga', example: 'World population', icon: Binary },
    { exp: 6, name: 'Mega', example: 'City population', icon: Binary },
    { exp: 3, name: 'Kilo', example: 'Building height (m)', icon: Ruler },
    { exp: 0, name: 'Base', example: 'Human height (m)', icon: Ruler },
    { exp: -3, name: 'Milli', example: 'Thickness of hair (m)', icon: Ruler },
    { exp: -6, name: 'Micro', example: 'Bacteria size (m)', icon: Microscope },
    { exp: -9, name: 'Nano', example: 'DNA width (m)', icon: Microscope },
    { exp: -12, name: 'Pico', example: 'Atom size (m)', icon: Atom },
    { exp: -15, name: 'Femto', example: 'Proton size (m)', icon: Atom },
  ];
  
  const closestScale = scales.reduce((prev, curr) => 
    Math.abs(curr.exp - exponent) < Math.abs(prev.exp - exponent) ? curr : prev
  );
  
  return (
    <div className="bg-muted/30 rounded-xl p-4 border">
      <h4 className="text-sm font-medium text-muted-foreground mb-3 text-center">Size Scale Reference</h4>
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {scales.map((scale) => {
          const Icon = scale.icon;
          const isActive = scale.exp === closestScale.exp;
          return (
            <div 
              key={scale.exp}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                isActive ? 'bg-teal-500/20 border border-teal-500/40' : 'hover:bg-muted/50'
              }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? 'text-teal-500' : 'text-muted-foreground'}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`font-medium text-sm ${isActive ? 'text-teal-500' : 'text-foreground'}`}>
                    10{toSuperscript(scale.exp)}
                  </span>
                  <span className="text-xs text-muted-foreground">({scale.name})</span>
                </div>
                <span className="text-xs text-muted-foreground">{scale.example}</span>
              </div>
              {isActive && (
                <Badge className="bg-teal-500 text-white text-xs">Your number</Badge>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

function ScientificNotationConverter() {
  const [mode, setMode] = useState('toScientific'); // 'toScientific' or 'toStandard'
  const [standardInput, setStandardInput] = useState('300000000');
  const [coefficient, setCoefficient] = useState(3);
  const [exponent, setExponent] = useState(8);
  const [expandedSteps, setExpandedSteps] = useState(['step-1']);

  // Convert to scientific notation
  const scientificResult = useMemo(() => {
    const num = parseFloat(standardInput);
    if (isNaN(num)) return { coefficient: 0, exponent: 0, formatted: 'Invalid input' };
    return toScientificNotation(num);
  }, [standardInput]);

  // Convert to standard form
  const standardResult = useMemo(() => {
    return fromScientificNotation(coefficient, exponent);
  }, [coefficient, exponent]);

  const handleReset = useCallback(() => {
    if (mode === 'toScientific') {
      setStandardInput('300000000');
    } else {
      setCoefficient(3);
      setExponent(8);
    }
  }, [mode]);

  // Quick examples for to scientific
  const quickStandard = [
    { val: '300000000', label: '3×10⁸ (Speed of light m/s)' },
    { val: '0.000001', label: '1×10⁻⁶ (Micrometer)' },
    { val: '6022000000000000000000000', label: '6.02×10²³ (Avogadro)' },
    { val: '0.00000000016', label: '1.6×10⁻¹⁰ (Electron charge)' },
    { val: '1000000', label: '10⁶ (Million)' },
    { val: '0.001', label: '10⁻³ (Thousandth)' },
  ];

  // Quick examples for to standard
  const quickScientific = [
    { coef: 3, exp: 8, label: 'Speed of light' },
    { coef: 6.02, exp: 23, label: 'Avogadro\'s number' },
    { coef: 1.6, exp: -19, label: 'Electron charge' },
    { coef: 9.8, exp: 0, label: 'Gravity (m/s²)' },
    { coef: 3.14, exp: 0, label: 'Pi (approx)' },
    { coef: 1, exp: -9, label: 'Nanometer' },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center">
            <Binary className="w-6 h-6 text-teal-500" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Scientific Notation Converter</h1>
            <p className="text-muted-foreground">Convert between standard form and scientific notation</p>
          </div>
        </div>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Calculator Panel */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:sticky lg:top-24 lg:self-start"
        >
          <Card className="rounded-2xl p-6 border bg-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-teal-500" />
                <span className="font-semibold text-foreground">Converter</span>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8" 
                onClick={handleReset} 
                title="Reset"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            {/* Mode Toggle */}
            <Tabs value={mode} onValueChange={setMode} className="mb-6">
              <TabsList className="w-full">
                <TabsTrigger value="toScientific" className="flex-1 text-xs">
                  Standard → Scientific
                </TabsTrigger>
                <TabsTrigger value="toStandard" className="flex-1 text-xs">
                  Scientific → Standard
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <AnimatePresence mode="wait">
              {mode === 'toScientific' ? (
                <motion.div
                  key="toScientific"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  {/* Standard Number Input */}
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Enter Number (Standard Form)</Label>
                    <Input
                      type="text"
                      value={standardInput}
                      onChange={(e) => setStandardInput(e.target.value)}
                      className="border text-xl font-mono h-14"
                      placeholder="300000000"
                    />
                  </div>

                  {/* Quick Examples */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">Quick Examples</Label>
                    <div className="space-y-2">
                      {quickStandard.slice(0, 4).map((ex, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          className={`w-full justify-start text-xs rounded-lg transition-all ${standardInput === ex.val ? 'bg-teal-500 text-white border-teal-500' : ''}`}
                          onClick={() => setStandardInput(ex.val)}
                        >
                          {ex.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Result */}
                  <div className="pt-4 border-t border-border">
                    <Label className="text-sm font-medium text-foreground mb-2 block">Scientific Notation</Label>
                    <div className="rounded-xl p-4 bg-teal-500/10 border border-teal-500/30">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-muted-foreground">Result</span>
                        <CopyButton text={scientificResult.formatted} />
                      </div>
                      <span className="font-mono text-xl font-bold text-foreground">
                        {scientificResult.formatted}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="toStandard"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  {/* Coefficient Input */}
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Coefficient (a in a × 10ⁿ)</Label>
                    <Input
                      type="number"
                      value={coefficient}
                      onChange={(e) => setCoefficient(parseFloat(e.target.value) || 0)}
                      step="0.01"
                      className="border text-xl font-mono h-14"
                      placeholder="3"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Should be between 1 and 10</p>
                  </div>

                  {/* Exponent Input */}
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Exponent (n in 10ⁿ)</Label>
                    <Input
                      type="number"
                      value={exponent}
                      onChange={(e) => setExponent(parseInt(e.target.value) || 0)}
                      className="border text-xl font-mono h-14"
                      placeholder="8"
                    />
                  </div>

                  {/* Quick Examples */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">Quick Examples</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {quickScientific.slice(0, 4).map((ex, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          className={`text-xs rounded-lg transition-all ${coefficient === ex.coef && exponent === ex.exp ? 'bg-teal-500 text-white border-teal-500' : ''}`}
                          onClick={() => { setCoefficient(ex.coef); setExponent(ex.exp); }}
                        >
                          {ex.coef}×10{toSuperscript(ex.exp)}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Result */}
                  <div className="pt-4 border-t border-border">
                    <Label className="text-sm font-medium text-foreground mb-2 block">Standard Form</Label>
                    <div className="rounded-xl p-4 bg-teal-500/10 border border-teal-500/30">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-muted-foreground">{coefficient} × 10{toSuperscript(exponent)}</span>
                        <CopyButton text={standardResult.toString()} />
                      </div>
                      <span className="font-mono text-xl font-bold text-foreground break-all">
                        {formatStandardNumber(standardResult)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </motion.div>

        {/* Center/Right Column - Visualization & Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Visualization Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="rounded-2xl p-6 border bg-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <ArrowLeftRight className="w-4 h-4 text-teal-500" />
                  Conversion Visualization
                  <Badge variant="secondary" className="ml-2 text-xs">Real-time</Badge>
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Visual Representation */}
                <DecimalMoveVisual 
                  originalNumber={standardInput}
                  coefficient={mode === 'toScientific' ? scientificResult.coefficient : coefficient}
                  exponent={mode === 'toScientific' ? scientificResult.exponent : exponent}
                  mode={mode}
                />

                {/* Scale Comparison */}
                <ScaleComparison 
                  exponent={mode === 'toScientific' ? scientificResult.exponent : exponent}
                />
              </div>
            </Card>
          </motion.div>

          {/* What is Scientific Notation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Card className="rounded-2xl p-6 border bg-card">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-teal-500" />
                What is Scientific Notation?
              </h2>
              
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-teal-500/10 border border-teal-500/20">
                  <h3 className="font-bold text-lg text-teal-600 mb-2">The Format</h3>
                  <div className="text-center text-2xl font-mono my-4">
                    <span className="text-foreground">a × 10</span>
                    <sup className="text-teal-500">n</sup>
                  </div>
                  <p className="text-muted-foreground text-center">
                    Where <strong>1 ≤ a &lt; 10</strong> and <strong>n</strong> is an integer
                  </p>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-muted/30 rounded-lg p-4">
                    <h4 className="font-medium text-foreground mb-2">For Large Numbers</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• Move decimal left until 1 ≤ a &lt; 10</li>
                      <li>• Count moves = positive exponent</li>
                      <li>• Example: 5,000 = 5 × 10³</li>
                    </ul>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <h4 className="font-medium text-foreground mb-2">For Small Numbers</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• Move decimal right until 1 ≤ a &lt; 10</li>
                      <li>• Count moves = negative exponent</li>
                      <li>• Example: 0.005 = 5 × 10⁻³</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Step-by-Step Solution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="rounded-2xl p-6 border bg-card">
              <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
                <BookOpen className="w-4 h-4 text-teal-500" />
                Step-by-Step Solution
              </h2>

              <Accordion type="multiple" value={expandedSteps} onValueChange={setExpandedSteps}>
                {mode === 'toScientific' ? (
                  <>
                    <AccordionItem value="step-1" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">1</span>
                          <span className="font-medium">Identify the number</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <p className="text-muted-foreground">
                            Your input: <span className="font-mono text-foreground font-semibold bg-secondary px-2 py-0.5 rounded">{standardInput}</span>
                          </p>
                          <div className="bg-secondary/50 rounded-lg p-4">
                            <p className="text-sm">
                              {parseFloat(standardInput) >= 1 
                                ? `This is a ${parseFloat(standardInput) >= 1000 ? 'large' : 'regular'} number (≥ 1)` 
                                : `This is a small number (< 1)`}
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-2" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">2</span>
                          <span className="font-medium">Find the coefficient</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <p className="text-muted-foreground">
                            Move the decimal point to get a number between 1 and 10:
                          </p>
                          <div className="bg-secondary/50 rounded-lg p-4 font-mono">
                            <p>{standardInput} → <span className="text-teal-500 font-bold">{scientificResult.coefficient}</span></p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-3" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">3</span>
                          <span className="font-medium">Count decimal places moved</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <p className="text-muted-foreground">
                            The decimal point moved <span className="text-teal-500 font-bold">{Math.abs(scientificResult.exponent)}</span> places to the {scientificResult.exponent >= 0 ? 'left' : 'right'}.
                          </p>
                          <div className="bg-secondary/50 rounded-lg p-4">
                            <p className="text-sm">
                              Exponent = <span className="text-teal-500 font-bold">{scientificResult.exponent}</span>
                              {scientificResult.exponent >= 0 ? ' (positive for large numbers)' : ' (negative for small numbers)'}
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-4" className="border-0">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">4</span>
                          <span className="font-medium">Write in scientific notation</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11">
                          <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/30">
                            <p className="text-lg font-mono">
                              <span className="text-muted-foreground">{standardInput} = </span>
                              <span className="text-2xl font-bold text-emerald-600">{scientificResult.formatted}</span>
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </>
                ) : (
                  <>
                    <AccordionItem value="step-1" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">1</span>
                          <span className="font-medium">Identify the parts</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <div className="bg-secondary/50 rounded-lg p-4">
                            <p className="text-sm">Coefficient (a): <span className="font-bold text-teal-500">{coefficient}</span></p>
                            <p className="text-sm">Exponent (n): <span className="font-bold text-teal-500">{exponent}</span></p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-2" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">2</span>
                          <span className="font-medium">Calculate 10ⁿ</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <div className="bg-secondary/50 rounded-lg p-4 font-mono">
                            <p>10{toSuperscript(exponent)} = {formatStandardNumber(Math.pow(10, exponent))}</p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-3" className="border-0">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">3</span>
                          <span className="font-medium">Multiply to get standard form</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <div className="bg-secondary/50 rounded-lg p-4 font-mono">
                            <p>{coefficient} × {formatStandardNumber(Math.pow(10, exponent))} = {formatStandardNumber(standardResult)}</p>
                          </div>
                          <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/30">
                            <p className="text-lg font-mono">
                              <span className="text-muted-foreground">{coefficient} × 10{toSuperscript(exponent)} = </span>
                              <span className="text-2xl font-bold text-emerald-600">{formatStandardNumber(standardResult)}</span>
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </>
                )}
              </Accordion>
            </Card>
          </motion.div>

          {/* Operations with Scientific Notation */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-teal-500" />
              Operations with Scientific Notation
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { name: 'Multiplication', formula: '(a × 10ᵐ) × (b × 10ⁿ) = (a×b) × 10ᵐ⁺ⁿ', desc: 'Multiply coefficients, add exponents' },
                { name: 'Division', formula: '(a × 10ᵐ) ÷ (b × 10ⁿ) = (a÷b) × 10ᵐ⁻ⁿ', desc: 'Divide coefficients, subtract exponents' },
                { name: 'Addition/Subtraction', formula: 'Same exponent required first', desc: 'Convert to same power of 10, then add/subtract coefficients' },
                { name: 'Powers', formula: '(a × 10ⁿ)ᵖ = aᵖ × 10ⁿˣᵖ', desc: 'Raise coefficient to power, multiply exponent' },
              ].map((rule, i) => (
                <div key={i} className="bg-muted/30 rounded-xl p-4">
                  <span className="text-xs text-muted-foreground block mb-1">{rule.name}</span>
                  <code className="font-mono text-sm text-foreground block mb-2">{rule.formula}</code>
                  <p className="text-xs text-muted-foreground">{rule.desc}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Common Mistakes */}
          <Card className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Common Mistakes to Avoid
            </h2>
            <ul className="space-y-3">
              {[
                'Coefficient not between 1 and 10 (e.g., writing 45 × 10³ instead of 4.5 × 10⁴)',
                'Wrong sign on exponent (positive for large, negative for small numbers)',
                'Adding exponents when adding numbers (you can only add exponents when multiplying)',
                'Forgetting to adjust coefficient when changing exponent',
                'Moving decimal the wrong direction when converting',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* FAQ Section */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {scientificNotationFAQs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-medium text-foreground">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>

          {/* Real World Applications */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Real-World Applications</h2>
            <ul className="space-y-3">
              {[
                'Astronomy: Distances between stars and galaxies (light-years = ~9.46 × 10¹² km)',
                'Chemistry: Avogadro\'s number (6.022 × 10²³) for counting atoms and molecules',
                'Physics: Speed of light (3 × 10⁸ m/s) and Planck\'s constant (6.63 × 10⁻³⁴ J·s)',
                'Biology: Sizes of cells, bacteria, and viruses (micrometers to nanometers)',
                'Finance: National debts and GDP figures in trillions (10¹²)',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <ArrowRight className="w-4 h-4 text-teal-500 mt-1 flex-shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default ScientificNotationConverter;
